import React, { useEffect, useMemo } from "react";
import AdminPortal from "./AdminPortal";
import { Eye, LayoutTemplate, Share2, X } from "lucide-react";
import { resolveAssetDisplayUrl } from "../../lib/assetUrl";

export default function AssetContextPreviewModal({ field, data, onClose }) {
  const siteName = (data.siteName || "").trim() || "— Chưa nhập tên site —";
  const siteUrl = (data.siteUrl || "").trim() || "https://…";

  const logoDisplay = resolveAssetDisplayUrl(data.logo?.url, data.logo?.key);
  const faviconDisplay = resolveAssetDisplayUrl(data.favicon?.url, data.favicon?.key);
  const ogDisplay = resolveAssetDisplayUrl(data.ogImage?.url, data.ogImage?.key);
  const bgDisplay = resolveAssetDisplayUrl(data.background?.url, data.background?.key);

  const title = useMemo(() => {
    if (field === "logo") return "Preview logo trên site";
    if (field === "favicon") return "Preview favicon trên trình duyệt";
    if (field === "ogImage") return "Preview OG khi chia sẻ link";
    if (field === "background") return "Preview background";
    return "Preview";
  }, [field]);

  const seoTitle =
    (data.homepageSeoTitle || "").trim() || `${siteName} – Khám phá địa điểm, ẩm thực & văn hoá`;
  const seoDesc =
    (data.homepageSeoDescription || "").trim() ||
    "Đoạn mô tả SEO sẽ hiển thị dưới tiêu đề trên Google khi bạn nhập ở mục SEO trang chủ.";

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AdminPortal>
      <div
        className="fixed inset-0 z-[998] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-6"
        onClick={onClose}
      >
        <div
          className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-admin-border bg-admin-surface px-4 py-3 sm:px-5">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-admin-primary text-white shadow-sm ring-1 ring-black/[0.04]">
                <Eye size={17} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-admin-text">{title}</p>
                <p className="truncate text-[11px] text-admin-textMuted">
                  Xem nhanh “xuất hiện ở đâu” cho ảnh vừa upload.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:bg-admin-muted hover:text-admin-text"
            >
              <X size={17} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-admin-muted/30 p-4 sm:p-5">
            {field === "logo" && (
              <div className="space-y-3">
                <p className="text-xs text-admin-textMuted">
                  Logo thường nằm ở header / footer. Dưới đây là mô phỏng.
                </p>
                <div className="overflow-hidden rounded-xl border border-white/10 shadow-lg ring-1 ring-black/5">
                  <header className="border-b border-white/10 bg-primary/95 px-3 py-2.5 backdrop-blur-md sm:px-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {logoDisplay ? (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-white/10">
                            <img
                              src={logoDisplay}
                              alt=""
                              className="h-full w-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10">
                            <LayoutTemplate size={17} className="text-white" />
                          </div>
                        )}
                        <div className="min-w-0 leading-tight">
                          <p className="truncate text-base font-bold tracking-tight text-white sm:text-lg">
                            {siteName}
                          </p>
                          <p className="hidden text-[10px] uppercase tracking-[0.2em] text-white/55 sm:block">
                            Khám phá thành phố sương mù
                          </p>
                        </div>
                      </div>
                      <div className="hidden shrink-0 gap-3 text-[11px] font-medium text-white/75 sm:flex">
                        <span>Bản đồ</span>
                        <span>Địa điểm</span>
                        <span>Liên hệ</span>
                      </div>
                    </div>
                  </header>
                  <footer className="bg-primary-hover px-4 py-4 text-card/90">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {logoDisplay ? (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5">
                            <img
                              src={logoDisplay}
                              alt=""
                              className="h-full w-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
                            <LayoutTemplate size={16} className="text-white/80" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-white">{siteName}</p>
                          <p className="mt-0.5 text-[11px] text-slate-400">
                            © {new Date().getFullYear()} {siteName}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px] text-slate-400">
                        <span className="rounded-full bg-white/5 px-2.5 py-1">Chính sách</span>
                        <span className="rounded-full bg-white/5 px-2.5 py-1">Liên hệ</span>
                        <span className="rounded-full bg-white/5 px-2.5 py-1">Facebook</span>
                      </div>
                    </div>
                  </footer>
                </div>
              </div>
            )}

            {field === "favicon" && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  Favicon hiển thị ở tab trình duyệt và bookmark.
                </p>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  {/* Faux browser tab strip */}
                  <div className="border-b border-slate-200 bg-admin-muted px-3 pt-3">
                    <div className="flex items-end gap-2">
                      <div className="flex h-9 w-[280px] max-w-full items-center gap-2 rounded-t-xl border border-slate-200 bg-white px-3 shadow-sm">
                        {faviconDisplay ? (
                          <img
                            src={faviconDisplay}
                            alt=""
                            className="h-4 w-4 shrink-0 object-contain"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-slate-300 text-[9px] font-bold text-white">
                            ?
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-700">
                          {seoTitle}
                        </span>
                        <span className="flex h-5 w-5 items-center justify-center rounded-md text-slate-400">
                          ×
                        </span>
                      </div>
                      <div className="mb-1 flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm">
                        +
                      </div>
                    </div>
                  </div>

                  {/* Content area hint */}
                  <div className="bg-white p-4">
                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-[11px] font-semibold text-slate-500">Tab title</span>
                      <span className="text-[11px] text-slate-400">·</span>
                      <span className="truncate text-[11px] text-slate-500">{siteUrl}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {field === "ogImage" && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  Mô phỏng thẻ xem trước khi chia sẻ (Facebook/Zalo/Messenger…).
                </p>
                <div className="mx-auto max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="aspect-[1.91/1] w-full bg-slate-200">
                    {ogDisplay ? (
                      <img src={ogDisplay} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-slate-400">
                        Chưa có ảnh OG
                      </div>
                    )}
                  </div>
                  <div className="border-t border-slate-100 bg-slate-50 px-3 py-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">
                      {(siteUrl || "").replace(/^https?:\/\//, "").split("/")[0] || "website"}
                    </p>
                    <p className="line-clamp-2 text-sm font-semibold text-slate-900">{seoTitle}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">{seoDesc}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <Share2 size={14} className="mt-0.5 shrink-0" />
                  <span>Ảnh và text thực tế phụ thuộc nền tảng; đây là mô phỏng gần đúng.</span>
                </div>
              </div>
            )}

            {field === "background" && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Mô phỏng background (không phải 1:1 với site).</p>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="relative aspect-[16/9] w-full bg-slate-200">
                    {bgDisplay ? (
                      <img src={bgDisplay} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-slate-400">
                        Chưa có background
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-black/45" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-sm font-semibold text-white">{siteName}</p>
                      <p className="mt-1 text-xs text-white/75">Khối nội dung sẽ nằm “trên” background</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminPortal>
  );
}

